/**
 * Small cells for the Feedback inbox tables (12 Oct).
 *
 * Rusty: "format this page better so it's on one screen". Every view's table
 * now fits 1366–1920 px without scrolling sideways: dates read "9 Oct 06:53",
 * the rating is an icon, long text is clamped to two lines and the full text
 * opens in a detail row under the row when the cell is clicked
 * (`DataGrid`'s `renderCellDetail`). The grid's own scroll area is capped at
 * the viewport, so its header stays put while the rows scroll.
 */

import { Icon, ThumbsDown, ThumbsUp } from "@norican/icons";
import { Badge, cn, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { parseDateInput } from "@/components/common";
import { useLanguage, useT } from "@/i18n";
import type { FeedbackRating, FeedbackReasonCode } from "@/lib/api";

/**
 * Caps the grid's scroll area at the viewport so its sticky header stays in
 * view; the page header and filters stay above it. `DataGrid`'s root clips and
 * its first child is the scroll area, whose own `max-height: 100%` has nothing
 * to resolve against, so the cap goes on that child.
 */
export const COMPACT_GRID_CLASS = "[&>div]:max-h-[max(24rem,calc(100dvh-14rem))]";

const SHORT_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function shortFormatter(locale: string, withYear: boolean): Intl.DateTimeFormat {
  const key = `${locale}|${withYear ? "y" : ""}`;
  const cached = SHORT_FORMATTERS.get(key);
  if (cached !== undefined) {
    return cached;
  }
  const created = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
    hour: "2-digit",
    minute: "2-digit",
  });
  SHORT_FORMATTERS.set(key, created);
  return created;
}

/** "9 Oct 06:53" (the year only when it is not this year); the full date on hover. */
export function ShortDateTime({ value }: { value: string | null | undefined }) {
  const { locale } = useLanguage();
  const parsed = parseDateInput(value);
  if (parsed === null) {
    return null;
  }
  const withYear = parsed.getFullYear() !== new Date().getFullYear();
  const text = shortFormatter(locale, withYear).format(parsed).replace(",", "");
  const full = new Intl.DateTimeFormat(locale, { dateStyle: "full", timeStyle: "short" }).format(
    parsed,
  );
  return (
    <time dateTime={parsed.toISOString()} title={full} className="whitespace-nowrap tabular-nums">
      {text}
    </time>
  );
}

/** 👍 or 👎 as an icon, named for screen readers and on hover. */
export function RatingIcon({ rating }: { rating: FeedbackRating }) {
  const { t } = useT("assistant");
  const helpful = rating === "HELPFUL";
  const label = helpful ? t("feedback.helpful") : t("feedback.notHelpful");
  return (
    <Badge
      tone={helpful ? "success" : "danger"}
      variant="subtle"
      size="sm"
      aria-label={label}
      title={label}
    >
      <Icon icon={helpful ? ThumbsUp : ThumbsDown} size="xs" />
    </Badge>
  );
}

/** The 👎 reasons as small chips. */
export function ReasonChips({
  codes,
}: {
  codes: readonly FeedbackReasonCode[] | null | undefined;
}) {
  const { t } = useT("assistant");
  if ((codes ?? []).length === 0) {
    return null;
  }
  return (
    <span className="flex flex-wrap gap-0.5 whitespace-normal">
      {(codes ?? []).map((code) => (
        <Badge key={code} tone="neutral" variant="outline" size="sm">
          {t(`feedback.reason.${code}`)}
        </Badge>
      ))}
    </span>
  );
}

/**
 * Long text clamped to a few lines. Inside a `renderCellDetail` column the
 * whole cell is a button that opens the full text below the row.
 */
export function Clamp({
  children,
  lines = 2,
  className,
}: {
  children: ReactNode;
  lines?: 2 | 3;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "min-w-0 text-start break-words whitespace-normal",
        lines === 2 ? "line-clamp-2" : "line-clamp-3",
        className,
      )}
    >
      {children}
    </span>
  );
}

/** One labelled block in a row's detail panel. */
export function DetailField({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <Typography variant="caption" tone="muted" className="tracking-wide uppercase">
        {label}
      </Typography>
      <div className="text-sm break-words whitespace-pre-wrap">{children}</div>
    </div>
  );
}

/** The detail row's layout: up to three columns of full text side by side. */
export function DetailPanel({ children }: { children: ReactNode }) {
  return <div className="grid gap-4 py-1 md:grid-cols-3">{children}</div>;
}
